/**
 * Where a provider credential came from, and how much of it to admit to.
 *
 * @module
 */
import { toastManager } from "../ui/toast";
import {
  PROVIDER_CREDENTIAL_SCOPE_LABELS,
  PROVIDER_CREDENTIAL_SOURCE_LABELS,
  PROVIDER_RUNTIME_LABELS,
} from "./providerAuthCopy";
import { ProviderAuthSession, ServerProvider } from "@cmmd-forge/contracts";

export function isDebugDisclosureEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("forge:debug-mode") === "true";
  } catch {
    return false;
  }
}

export function formatProviderProvenanceSummary(provider: ServerProvider | undefined) {
  if (!isDebugDisclosureEnabled()) return null;
  const provenance = provider?.auth.provenance;
  if (!provenance) return null;
  const scope = PROVIDER_CREDENTIAL_SCOPE_LABELS[provenance.credentialScope];
  const runtime = PROVIDER_RUNTIME_LABELS[provenance.runtimeRole];
  const inherited = provenance.isHostInherited ? "Host inherited" : null;
  return [scope, runtime, inherited].filter(Boolean).join(" · ");
}

export function formatProviderProvenanceDetails(provider: ServerProvider | undefined) {
  if (!isDebugDisclosureEnabled()) return [];
  const provenance = provider?.auth.provenance;
  if (!provenance) return [];
  return [
    ["Runtime", PROVIDER_RUNTIME_LABELS[provenance.runtimeRole]],
    ["Credential scope", PROVIDER_CREDENTIAL_SCOPE_LABELS[provenance.credentialScope]],
    ["Source", PROVIDER_CREDENTIAL_SOURCE_LABELS[provenance.source]],
    ["Account", provenance.accountLabel],
    ["Home/config", provenance.homePath],
    ["Binary/endpoint", provenance.binaryPath],
    ["Injected", provenance.injectedAt],
    ["Host inherited", provenance.isHostInherited ? "Yes" : "No"],
  ].filter((entry): entry is [string, string] => Boolean(entry[1]));
}

export function isProviderAuthSessionTerminal(session: ProviderAuthSession | null): boolean {
  return (
    session?.state === "completed" ||
    session?.state === "failed" ||
    session?.state === "canceled" ||
    session?.state === "expired"
  );
}

export async function copyProviderAuthText(
  value: string,
  label: string,
  onManualCopyNeeded?: (value: string) => void,
) {
  if (!value) return;

  try {
    if (typeof window !== "undefined" && window.isSecureContext && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      toastManager.add({ type: "success", title: `${label} copied` });
      return;
    }
  } catch (error) {
    onManualCopyNeeded?.(value);
    toastManager.add({
      type: "error",
      title: `Could not copy ${label.toLowerCase()}`,
      description: error instanceof Error ? error.message : "Showing the value instead.",
    });
    return;
  }

  onManualCopyNeeded?.(value);
  toastManager.add({
    type: "error",
    title: "Clipboard copy unavailable",
    description: "Showing the value instead.",
  });
}
